import React from 'react';
import { createRoot } from 'react-dom/client';
import { i18n, init as i18nInit } from 'src/i18n';
import { AuthToken } from './modules/auth/authToken';
import TenantService from './modules/tenant/tenantService';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import AuthService from 'src/modules/auth/authService';
import { AuthBootstrap } from 'src/modules/auth/authBootstrap';
import config from 'src/config';
import queryString from 'query-string';
import { ThemeProvider } from './theme/ThemeContext';
import DataDogErrorBoundary from 'src/datadog/DataDogErrorBoundary';
import { initDataDog } from 'src/datadog/datadogHelpers';
import { bootstrap } from './bootstrap';

AuthToken.applyFromLocationUrlIfExists();

const queryParams = queryString.parse(window.location.search);

const isPublicView = window.location.href.includes(
  `${config.eLabelPublicUrl.host}${config.eLabelPublicUrl.path}`,
);

// The current user request starts right away, in parallel with
// the tenant and translation requests below.
if (AuthToken.get()) {
  AuthBootstrap.start(() => AuthService.fetchMe()).catch(() => undefined);
}

// Tenant and translations load in parallel; the app bundle is
// imported only afterwards because it translates labels at
// module scope (its download is preloaded from index.html).
const appModule = await bootstrap({
  loadTenant: () => TenantService.fetchAndApply(),
  loadTranslations: () => i18nInit(queryParams?.lang as string),
  loadApp: () =>
    isPublicView ? import('./public-elabel/PublicElabelApp') : import('./App'),
});

const App = appModule.default;

document.title = i18n('app.title');

const currentSettings = AuthCurrentTenant.getSettings();
const container = document.getElementById('root');
const root = createRoot(container as Element);

root.render(
  <ThemeProvider initTheme={currentSettings?.theme}>
    <DataDogErrorBoundary>
      <App />
    </DataDogErrorBoundary>
  </ThemeProvider>,
);

// Monitoring is not needed for the first paint
const scheduleIdle =
  window.requestIdleCallback ?? (callback => window.setTimeout(callback, 0));
scheduleIdle(() => initDataDog());
